/**
 * RFQ Navigation Utility
 * Helper functions for navigating between RFQ detail pages
 */

export interface RFQNavigationInfo {
  currentPage: string;
  previousPage: string | null;
  nextPage: string | null;
  baseRoute: string;
  requestId: string;
}

/**
 * Get navigation info for current RFQ page
 */
export const getRFQNavigationInfo = (currentRoute: string): RFQNavigationInfo | null => {
  // Remove query parameters
  const routeWithoutQuery = currentRoute.split('?')[0];

  // Extract request ID and page type
  // Handle routes like /dashboard/user/rfq/{id}/communication/{supplierId}
  // Updated to support both 'open' and 'finalized' segments
  const rfqDetailMatch = routeWithoutQuery.match(/\/dashboard\/rfqs\/(open|finalized)\/([^/]+)(?:\/([^/]+))?/);
  const userRfqMatch = routeWithoutQuery.match(/\/dashboard\/user\/rfq\/([^/]+)(?:\/([^/]+))?/);
  const adminRequestMatch = routeWithoutQuery.match(/\/dashboard\/admin\/request\/([^/]+)(?:\/([^/]+))?/);

  const match = rfqDetailMatch || userRfqMatch || adminRequestMatch;
  if (!match) {
    return null;
  }

  // If it matched rfqDetailMatch, the first group is the status (open/finalized), second is ID
  // If it matched others, first group is ID
  let requestId: string;
  let pageSeg: string | undefined;
  let statusSegment = 'open'; // default

  if (rfqDetailMatch) {
    statusSegment = match[1];
    requestId = match[2];
    pageSeg = match[3];
  } else {
    requestId = match[1];
    pageSeg = match[2];
  }

  // Get the page type
  // For routes like /communication/{supplierId}, we only want 'communication'
  const secondSegment = pageSeg;
  const page = secondSegment || 'detail';

  // Determine base route
  const isBasicPlanRoute = routeWithoutQuery.startsWith('/dashboard/user/rfq/');

  // Dynamic base route based on status segment
  let baseRoute = '/dashboard/rfqs/open';
  if (isBasicPlanRoute) {
    baseRoute = '/dashboard/user/rfq';
  } else if (rfqDetailMatch) {
    baseRoute = `/dashboard/rfqs/${statusSegment}`;
  } else if (routeWithoutQuery.startsWith('/dashboard/admin/request/')) {
    // Legacy or direct admin route
    baseRoute = '/dashboard/rfqs/open';
  }

  // Define page order (user routes don't have product-overview)
  const isUserRoute = isBasicPlanRoute;
  const pages = isUserRoute
    ? ['detail', 'suppliers', 'communication', 'quotes-review']
    : ['detail', 'products', 'suppliers', 'communication', 'quotes-review'];

  const currentIndex = pages.indexOf(page);
  if (currentIndex === -1) {
    return null;
  }

  const previousPage = currentIndex > 0 ? pages[currentIndex - 1] : null;
  const nextPage = currentIndex < pages.length - 1 ? pages[currentIndex + 1] : null;

  return {
    currentPage: page,
    previousPage,
    nextPage,
    baseRoute,
    requestId
  };
};

/**
 * Get route for a specific RFQ page
 */
export const getRFQPageRoute = (baseRoute: string, requestId: string, page: string): string => {
  if (page === 'detail') {
    return `${baseRoute}/${requestId}`;
  }
  return `${baseRoute}/${requestId}/${page}`;
};

/**
 * Get display name for a page
 */
export const getRFQPageName = (page: string): string => {
  const pageNames: Record<string, string> = {
    'detail': 'RFQ Review',
    'products': 'Product Overview',
    'suppliers': 'Search Suppliers',
    'communication': 'Communication',
    'quotes-review': 'Quotes Review',
    'manual-products': 'Manual Products'
  };
  return pageNames[page] || page;
};











